import { Component } from 'react';
import { compose } from 'recompose';
import { ChartFragment } from 'models/api-response';
import { FormFieldInputType, FragmentType } from 'models/enum';
import { ChartActionsComponentProps, ChartComponentProps, ChartMetaFormField } from 'models/ui';
import { NAV_ID } from 'constants/app.constant';
import { appHelper, chartHelper } from 'helpers';
import { chartService } from 'services';
import { withChartLogic } from 'components/common';
import { FormField, SectionTitle } from './constants';
import DevelopmentalScreenView from './DevelopmentalScreenView';
import withAdmissionHistory from '../shared/withAdmissionHistory';

interface DevelopmentalScreenState {
  isLocked: boolean;
  fragment: ChartFragment;
  statusFragment: ChartFragment;
}

class DevelopmentalScreen extends Component<ChartComponentProps, DevelopmentalScreenState> {
  static displayName = 'DevelopmentalScreen';

  constructor(props) {
    super(props);
    this.state = {
      isLocked: false,
      fragment: null,
      statusFragment: null
    };
  }

  componentDidMount() {
    return appHelper.useLoader(
      this.props.loadChartData().then(({ data: fragments }) => {
        const chartFragment = fragments.find((fragment) => fragment.fragmentType !== FragmentType.STATUS) ?? null;
        const statusFragment = chartHelper.findStatusFragment(fragments, NAV_ID.DEVELOPMENTAL_SCREEN, NAV_ID.ADMISSION_HISTORY);
        this.setState({ fragment: chartFragment, statusFragment, isLocked: chartHelper.isChartLocked(fragments, this.props.selectedNavId) }, () => {
          this.props.initState(this.buildDefaultFormFields());
        });
      }),
      { errorMessage: 'can not load chart data' }
    );
  }

  buildDefaultFormFields = (): Map<string, ChartMetaFormField> => {
    const { createFormField, getFragmentContentIds } = chartHelper;
    const { fragment } = this.state;
    const dataMap = new Map();

    const radioChoices = [
      {
        formField: FormField.DEVELOPMENT_AGE,
        label: SectionTitle.DEVELOPMENT_AGE
      },
      {
        formField: FormField.CHRONOLOGICAL_AGE,
        label: SectionTitle.CHRONOLOGICAL_AGE
      },
      {
        formField: FormField.GROSS_MOTOR_SKILLS,
        label: SectionTitle.GROSS_MOTOR_SKILLS
      },
      {
        formField: FormField.FINE_MOTOR_SKILLS,
        label: SectionTitle.FINE_MOTOR_SKILLS
      },
      {
        formField: FormField.SENSORY_SKILLS,
        label: SectionTitle.SENSORY_SKILLS
      },
      {
        formField: FormField.VOCALIZATION_SKILLS,
        label: SectionTitle.VOCALIZATION_SKILLS
      },
      {
        formField: FormField.SOCIALIZATION_COGNITION,
        label: SectionTitle.SOCIALIZATION_COGNITION
      }
    ];

    radioChoices.forEach((radioChoice) => {
      dataMap.set(
        radioChoice.formField,
        createFormField({
          name: radioChoice.formField,
          type: FormFieldInputType.RADIO_CHOICE,
          label: radioChoice.label,
          contentIds: getFragmentContentIds(fragment, SectionTitle.DEVELOPMENT_SCREEN, radioChoice.formField)
        })
      );
    });

    return dataMap;
  };

  handleSaveClick = () => {
    this.props.handleSaveClick([this.buildFragment()], { afterSave: this.props.afterSave });
  };

  buildFragment = () => {
    const { buildPatientRecord } = chartService;
    const { formFieldMap } = this.props;
    const record = {
      chartTitle: 'Admission History',
      fragmentTitle: SectionTitle.DEVELOPMENT_SCREEN,
      records: [
        {
          sectionTitle: SectionTitle.DEVELOPMENT_SCREEN,
          records: [
            buildPatientRecord(formFieldMap, FormField.DEVELOPMENT_AGE),
            buildPatientRecord(formFieldMap, FormField.CHRONOLOGICAL_AGE),
            buildPatientRecord(formFieldMap, FormField.GROSS_MOTOR_SKILLS),
            buildPatientRecord(formFieldMap, FormField.FINE_MOTOR_SKILLS),
            buildPatientRecord(formFieldMap, FormField.SENSORY_SKILLS),
            buildPatientRecord(formFieldMap, FormField.VOCALIZATION_SKILLS),
            buildPatientRecord(formFieldMap, FormField.SOCIALIZATION_COGNITION)
          ]
        }
      ]
    };
    const cleanRecord = chartService.systemAssessment.removeEmptyRecords(record);
    const fragmentId = this.state.fragment?.fragmentId;
    const basicInfo = chartService.createBaseFragment({ fragmentId, chartingTime: this.props.chartingTime });
    return { ...basicInfo, chartData: cleanRecord } as ChartFragment;
  };

  handleCancelClick = () => {
    this.props.initState(this.buildDefaultFormFields());
    appHelper.scrollTop();
  };

  render() {
    const { isLocked, fragment, statusFragment } = this.state;
    const { formFieldMap, formSubmittedCount, handleDiscardClick, displayAuthoringData, enableDisplayRecordsButton, hasUnsavedChanges } = this.props;
    const chartActionsProps: ChartActionsComponentProps = {
      isLocked,
      saveButtonText: 'Save and Continue',
      cancelButtonText: 'Cancel',
      saveButtonHasIcon: true,
      onSaveClick: this.handleSaveClick,
      onCancelClick: () => handleDiscardClick(this.handleCancelClick),
      onDisplayRecordsClick: displayAuthoringData,
      enableSaveButton: hasUnsavedChanges,
      enableDisplayRecordsButton
    };

    const viewProps = {
      isLocked,
      fragment,
      statusFragment,
      chartActionsProps,
      formSubmittedCount,
      formFieldMap
    };
    return <DevelopmentalScreenView {...viewProps} />;
  }
}

export { DevelopmentalScreen as BaseDevelopmentalScreen };
export default compose(withChartLogic, withAdmissionHistory)(DevelopmentalScreen);
